import { type Action } from '@canvas/model/permissions';

/**
 * Is an action allowed?
 */
export type Allowance = {
  can: Action;
};

/**
 * Is an action not allowed?
 */
export type Disallowance = {
  cannot: Action;
};

/**
 * The set of claims a user currently has access to.
 */
type PolicyInput = Set<Action>;

/**
 * A policy must have only ONE defined outcome. You can either allow an action
 * to happen, or not allow an action to happen. If you want to define multiple
 * outcomes, don't - make another policy instead. This keeps them small,
 * testable, and reduces the probabilit of unintended errors.
 */
export type PolicyOutcome = Allowance | Disallowance | undefined;

/**
 * A rule is repsonsible for producing an outcome based upon the current state
 * of permissions a user has. These outcomes are then collapsed into one final
 * result, which is used by consumers of these methods.
 */
type Rule = (state: PolicyInput) => PolicyOutcome;

export type Policy = {
  /**
   * Unique identifier for this policy, based on which it can be enabled or disabled.
   */
  id: string;
  /**
   * Type of policy, determining behaviours around it.
   * @description persistent policies cannot be removed
   * @description transient policies can be removed
   */
  type: 'persistent' | 'transient';
  /**
   * An tag for identifying this policy as part of a group of policies.
   * @description Used by different modes of applications to add and remove
   * groups of policies as they are enabled or disabled.
   */
  tag?: string;
  /**
   * Evaluates the rule defining a policy to produce an outcome.
   * @param state current permissions state, containing current claims the user has
   * @returns outcome of the policy evaluation
   */
  rule: Rule;
};

export const isAllowance = (outcome: PolicyOutcome): outcome is Allowance => {
  return Boolean(outcome && 'can' in outcome);
};

export const isDisallowance = (outcome: PolicyOutcome): outcome is Disallowance => {
  return Boolean(outcome && 'cannot' in outcome);
};
